// re.rejouis.fr — /envoi/<code> : étiquette d'envoi vers l'atelier pour un partenaire, un
// fournisseur… sans compte. Le lien est créé au lab (espace Pros, « Étiquettes d'envoi »),
// avec un nombre d'étiquettes max et une date d'expiration vérifiés par le lab.
// Page autonome (main.jsx), en vouvoiement. Le nom saisi est l'expéditeur affiché sur
// l'étiquette ; l'e-mail reste chez nous, jamais sur l'étiquette.
import { useEffect, useState } from 'react';
import { Icons, LogoRe, Spinner } from '../components/ui.jsx';
import EtiquetteQr from '../components/EtiquetteQr.jsx';
import { envoiLibre } from '../lib/api.js';
import { suivre } from '../lib/posthog.js';

const POIDS = [[500, 'Moins de 500 g'], [1000, 'Jusqu’à 1 kg'], [2000, 'Jusqu’à 2 kg'], [5000, 'Jusqu’à 5 kg'], [10000, 'Jusqu’à 10 kg'], [20000, 'Jusqu’à 20 kg']];
const cle = (code) => `rj_envoi_${code}`;

function lireSauve(code) {
  try { return JSON.parse(localStorage.getItem(cle(code)) || 'null'); } catch { return null; }
}
function sauver(code, etiquette) {
  try { localStorage.setItem(cle(code), JSON.stringify(etiquette)); } catch { /* navigation privée */ }
}

function Champ({ label, aide, children }) {
  return (
    <label style={{ display: 'block' }}>
      <div className="t-overline" style={{ marginBottom: 6 }}>{label}</div>
      {children}
      {aide ? <div style={{ fontSize: 12, color: 'var(--stone)', marginTop: 4 }}>{aide}</div> : null}
    </label>
  );
}

export default function EnvoiLibre({ code }) {
  const [lien, setLien] = useState(null);
  const [erreurLien, setErreurLien] = useState(null);
  const [etiquette, setEtiquette] = useState(() => lireSauve(code));
  const [f, setF] = useState({ prenom: '', nom: '', societe: '', email: '', contenu: '', poidsGr: 1000 });
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState(null);

  useEffect(() => {
    document.title = 'Envoyer un colis à Rejouis';
    envoiLibre('infos', code)
      .then((r) => setLien(r.lien))
      .catch((e) => setErreurLien(e.message || 'Lien introuvable.'));
  }, [code]);

  const maj = (k) => (e) => setF({ ...f, [k]: e.target.value });

  async function creer(e) {
    e.preventDefault();
    setErreur(null);
    setEnvoi(true);
    try {
      const r = await envoiLibre('creer', code, { ...f, poidsGr: Number(f.poidsGr) });
      sauver(code, r.etiquette);
      setEtiquette(r.etiquette);
      suivre('envoi_libre_etiquette', { poids: Number(f.poidsGr) });
      window.scrollTo({ top: 0 });
    } catch (err) {
      setErreur(err.message || 'La création a échoué, réessayez dans un instant.');
    } finally {
      setEnvoi(false);
    }
  }

  async function pdf() {
    const r = await envoiLibre('pdf', code, { ref: etiquette.ref });
    sauver(code, r.etiquette);
    return { labelUrl: r.etiquette.bordereau };
  }

  let contenu;
  if (etiquette) {
    contenu = (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div className="card" style={{ padding: 16 }}>
          {etiquette.bordereau ? (
            <a className="btn btn-primary btn-block" href={etiquette.bordereau} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
              <Icons.download size={16} /> Imprimer l’étiquette
            </a>
          ) : (
            <EtiquetteQr vous qrCode={etiquette.qrCode} demanderEtiquettePdf={pdf} />
          )}
        </div>
        <div className="soft-card" style={{ padding: 14, fontSize: 13, color: 'var(--encre-doux)', lineHeight: 1.6 }}>
          <div style={{ fontWeight: 700, color: 'var(--noir)', marginBottom: 4 }}>Pour envoyer votre colis</div>
          1. Emballez le contenu et fermez bien le colis.<br />
          2. Déposez-le dans <strong>n’importe quel point relais Mondial Relay</strong> avec ce QR code.<br />
          Votre référence <strong>{etiquette.ref}</strong> figure déjà sur l’étiquette : pas besoin d’ajouter un mot.
        </div>
        {etiquette.suiviUrl ? (
          <a href={etiquette.suiviUrl} target="_blank" rel="noopener noreferrer" className="btn btn-tertiary btn-block" style={{ textDecoration: 'none' }}>
            <Icons.truck size={16} /> Suivre le colis
          </a>
        ) : null}
        <div style={{ fontSize: 12, color: 'var(--stone)', textAlign: 'center', lineHeight: 1.55 }}>
          Gardez cette page ou faites une capture du QR code. Merci pour votre envoi !
        </div>
      </div>
    );
  } else if (erreurLien) {
    contenu = <div className="card" style={{ padding: 20, textAlign: 'center', lineHeight: 1.6 }}>{erreurLien}</div>;
  } else if (!lien) {
    contenu = <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><Spinner size={28} /></div>;
  } else if (lien.etat !== 'ouvert') {
    contenu = <div className="card" style={{ padding: 20, textAlign: 'center', lineHeight: 1.6 }}>{lien.message}</div>;
  } else {
    contenu = (
      <form onSubmit={creer} className="card ph-no-capture" style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
          <Champ label="Prénom"><input className="field" value={f.prenom} onChange={maj('prenom')} autoComplete="given-name" /></Champ>
          <Champ label="Nom"><input className="field" value={f.nom} onChange={maj('nom')} autoComplete="family-name" required /></Champ>
        </div>
        <Champ label="Société (facultatif)"><input className="field" value={f.societe} onChange={maj('societe')} autoComplete="organization" /></Champ>
        <Champ label="E-mail" aide="Pour vous joindre si besoin. Il n’apparaît pas sur l’étiquette.">
          <input className="field" type="email" value={f.email} onChange={maj('email')} autoComplete="email" required />
        </Champ>
        <Champ label="Ce que vous envoyez">
          <textarea className="field" rows={3} value={f.contenu} onChange={maj('contenu')} required placeholder="Ex. 3 jouets en prêt, échantillons…" />
        </Champ>
        <Champ label="Poids approximatif du colis">
          <select className="field" value={f.poidsGr} onChange={maj('poidsGr')}>
            {POIDS.map(([g, l]) => <option key={g} value={g}>{l}</option>)}
          </select>
        </Champ>
        {erreur ? <div style={{ fontSize: 13, color: 'var(--rouge-sombre)' }}>{erreur}</div> : null}
        <button className="btn btn-primary btn-block" type="submit" disabled={envoi}>
          {envoi ? <Spinner size={18} /> : 'Créer mon étiquette'}
        </button>
        <div style={{ fontSize: 12, color: 'var(--stone)', textAlign: 'center', lineHeight: 1.55 }}>
          L’envoi est à notre charge. Vous recevez un QR code à présenter en point relais Mondial Relay.
        </div>
      </form>
    );
  }

  return (
    <div className="proto-root" style={{ minHeight: '100vh', background: 'var(--creme)' }}>
      <div style={{ maxWidth: 520, margin: '0 auto', padding: '20px 16px 48px' }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 18 }}><LogoRe size={20} /></div>
        <div className="t-display" style={{ fontSize: 26, textAlign: 'center' }}>Envoyer un colis à Rejouis</div>
        {lien?.libelle && !etiquette ? (
          <div style={{ textAlign: 'center', marginTop: 6, fontWeight: 600 }}>{lien.libelle}</div>
        ) : null}
        <div style={{ textAlign: 'center', fontSize: 13.5, color: 'var(--stone)', margin: '6px 0 18px', lineHeight: 1.55 }}>
          {etiquette ? 'Votre étiquette est prête.' : 'Dites-nous qui vous êtes et ce que vous envoyez : votre étiquette Mondial Relay est créée tout de suite.'}
        </div>
        {contenu}
      </div>
    </div>
  );
}
